import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import clsx from 'clsx';

/**
 * Pagination - Profesyonel, yeniden kullanılabilir sayfalama bileşeni
 * 
 * @param {number} currentPage - Mevcut sayfa (1-indexed)
 * @param {number} totalPages - Toplam sayfa sayısı
 * @param {function} onPageChange - Sayfa değiştiğinde çağrılır (page) => void
 * @param {number} totalItems - Toplam öğe sayısı (opsiyonel, bilgi gösterimi için)
 * @param {number} pageSize - Sayfa başına öğe sayısı (varsayılan: 10)
 * @param {boolean} showInfo - Sayfa bilgisi göster (opsiyonel)
 * @param {boolean} showFirstLast - İlk/Son sayfa butonları göster
 * @param {number} siblingCount - Mevcut sayfanın yanında gösterilecek sayfa sayısı
 * @param {string} className - Ek CSS sınıfları
 */
export default function Pagination({
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    totalItems,
    pageSize = 10,
    showInfo = true,
    showFirstLast = true,
    siblingCount = 1,
    className,
}) {
    // Sayfa aralığını hesapla
    const getPageNumbers = () => {
        const pages = [];
        const leftSibling = Math.max(currentPage - siblingCount, 1);
        const rightSibling = Math.min(currentPage + siblingCount, totalPages);

        // Sol tarafta ellipsis göster
        const showLeftEllipsis = leftSibling > 2;
        // Sağ tarafta ellipsis göster  
        const showRightEllipsis = rightSibling < totalPages - 1;

        // Her zaman ilk sayfayı göster
        if (leftSibling > 1) {
            pages.push(1);
            if (showLeftEllipsis) {
                pages.push('left-ellipsis');
            }
        }

        // Sibling sayfaları
        for (let i = leftSibling; i <= rightSibling; i++) {
            pages.push(i);
        }

        // Her zaman son sayfayı göster
        if (rightSibling < totalPages) {
            if (showRightEllipsis) {
                pages.push('right-ellipsis');
            }
            pages.push(totalPages);
        }

        return pages;
    };

    // Mobil görünüm için basit sayfa gösterimi
    const getMobilePageNumbers = () => {
        if (totalPages <= 3) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage === 1) return [1, 2, 'right-ellipsis', totalPages];
        if (currentPage === totalPages) return [1, 'left-ellipsis', totalPages - 1, totalPages];

        return [1, 'left-ellipsis', currentPage, 'right-ellipsis', totalPages];
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            onPageChange(page);
        }
    };

    // Hiç sayfa yoksa gösterme
    if (totalPages <= 1) {
        return showInfo && totalItems ? (
            <div className="flex justify-center py-3">
                <span className="text-sm text-text-muted">
                    Toplam {totalItems} kayıt
                </span>
            </div>
        ) : null;
    }

    const desktopPages = getPageNumbers();
    const mobilePages = getMobilePageNumbers();

    // Bilgi hesaplama
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems || currentPage * pageSize);

    return (
        <div className={clsx(
            'flex flex-col sm:flex-row items-center justify-between gap-3 py-3',
            className
        )}>
            {/* Bilgi Bölümü */}
            {showInfo && totalItems && (
                <div className="text-sm text-text-muted order-2 sm:order-1">
                    <span className="hidden sm:inline">
                        {startItem}-{endItem} / {totalItems} kayıt gösteriliyor
                    </span>
                    <span className="sm:hidden">
                        {currentPage}/{totalPages} sayfa
                    </span>
                </div>
            )}

            {/* Pagination Controls */}
            <nav
                className="flex items-center gap-1 order-1 sm:order-2"
                aria-label="Sayfalama"
            >
                {/* İlk Sayfa - Sadece desktop */}
                {showFirstLast && (
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className={clsx(
                            'hidden sm:flex items-center justify-center w-9 h-9 rounded-lg transition-all',
                            currentPage === 1
                                ? 'text-text-muted cursor-not-allowed opacity-50'
                                : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                        )}
                        aria-label="İlk sayfa"
                    >
                        <ChevronsLeft size={18} />
                    </button>
                )}

                {/* Önceki */}
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={clsx(
                        'flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-lg transition-all',
                        currentPage === 1
                            ? 'text-text-muted cursor-not-allowed opacity-50'
                            : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                    )}
                    aria-label="Önceki sayfa"
                >
                    <ChevronLeft size={20} />
                </button>

                {/* Sayfa Numaraları - Desktop */}
                <div className="hidden sm:flex items-center gap-1">
                    {desktopPages.map((page, index) => (
                        page === 'left-ellipsis' || page === 'right-ellipsis' ? (
                            <span
                                key={page}
                                className="w-9 h-9 flex items-center justify-center text-text-muted"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={clsx(
                                    'w-9 h-9 rounded-lg text-sm font-medium transition-all',
                                    page === currentPage
                                        ? 'bg-primary text-white shadow-sm'
                                        : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                                )}
                                aria-label={`Sayfa ${page}`}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        )
                    ))}
                </div>

                {/* Sayfa Numaraları - Mobile */}
                <div className="flex sm:hidden items-center gap-1">
                    {mobilePages.map((page, index) => (
                        page === 'left-ellipsis' || page === 'right-ellipsis' ? (
                            <span
                                key={page}
                                className="w-8 h-10 flex items-center justify-center text-text-muted text-sm"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={clsx(
                                    'w-10 h-10 rounded-lg text-sm font-medium transition-all',
                                    page === currentPage
                                        ? 'bg-primary text-white shadow-sm'
                                        : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                                )}
                                aria-label={`Sayfa ${page}`}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        )
                    ))}
                </div>

                {/* Sonraki */}
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={clsx(
                        'flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-lg transition-all',
                        currentPage === totalPages
                            ? 'text-text-muted cursor-not-allowed opacity-50'
                            : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                    )}
                    aria-label="Sonraki sayfa"
                >
                    <ChevronRight size={20} />
                </button>

                {/* Son Sayfa - Sadece desktop */}
                {showFirstLast && (
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className={clsx(
                            'hidden sm:flex items-center justify-center w-9 h-9 rounded-lg transition-all',
                            currentPage === totalPages
                                ? 'text-text-muted cursor-not-allowed opacity-50'
                                : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                        )}
                        aria-label="Son sayfa"
                    >
                        <ChevronsRight size={18} />
                    </button>
                )}
            </nav>
        </div>
    );
}
